import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, ShieldCheck, Sparkles, Trophy, Quote, MapPin, Phone, Mail, Navigation, Smartphone, Star, Calendar, Users, Building2 } from 'lucide-react';
import { Logo, Wordmark } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { Badge, KPI } from '@/components/ui/Card';
import { LocationPicker } from '@/components/LocationPicker';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';

// Where a signed-in user's "Sign In"/"Sign Up" CTAs should point instead —
// their own dashboard, not the auth forms they've already gotten past.
const dashboardPathFor = (user) => (user?.role === 'customer' ? '/app/bookings' : '/admin');

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/6170d9f7-0af6-44a4-9bd0-8b49a78661d1/images/734f0c5408bbc36fd5d93660b9e42d2c03d2e308d6896cb0466df742d64a70a4.png';

// Redesigned (Hudle-inspired search-first hero + stats bar + app-download
// section), still a Playo-style "find a venue near you" entry point since
// PaceSlot stays a badminton-focused multi-venue platform (courts/plans/pricing
// live per-venue, behind sign-up — see pages/customer/Venues.jsx) rather
// than Hudle's multi-sport model. Sport is still part of the search here
// because a venue can now run courts across more than one sport (see
// AdminSports.jsx) — the filter narrows to venues that actually offer the
// picked one (app.routers.academies._academy_ids_for_sport).
export default function Landing() {
  const [sports, setSports] = useState([]);
  const [sportId, setSportId] = useState('');
  const [venues, setVenues] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(true);
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState('');
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | locating | sorted | unavailable
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    api.get('/sports').then((r) => setSports(r.data)).catch(() => {});
  }, []);

  const fetchVenues = async (sport, coordsOverride) => {
    setLoadingVenues(true);
    const c = coordsOverride !== undefined ? coordsOverride : coords;
    try {
      const { data } = c
        ? await api.get('/academies/nearby', { params: { lat: c.lat, lng: c.lng, sport_id: sport || undefined } })
        : await api.get('/academies', { params: { sport_id: sport || undefined } });
      setVenues(data);
    } catch { /* keep previous list on failure */ } finally {
      setLoadingVenues(false);
    }
  };

  useEffect(() => { fetchVenues(sportId, coords); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSportChange = (id) => {
    setSportId(id);
    fetchVenues(id, coords);
  };

  const onLocationConfirmed = ({ address: picked, latitude, longitude }) => {
    setAddress(picked);
    setPickerOpen(false);
    setGeoStatus('sorted');
    const c = { lat: latitude, lng: longitude };
    setCoords(c);
    fetchVenues(sportId, c);
  };

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="landing-page">
      <Nav />
      <Hero
        sports={sports}
        sportId={sportId}
        onSportChange={onSportChange}
        address={address}
        geoStatus={geoStatus}
        onOpenLocation={() => setPickerOpen(true)}
      />
      <VenueDiscovery venues={venues} loading={loadingVenues} sportId={sportId} onClearSport={() => onSportChange('')} />
      <StatsBar />
      <FeatureHighlights />
      <Testimonials />
      <AppDownload />
      <CTASection />
      <Footer />

      <LocationPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={onLocationConfirmed}
        title="Select your location"
      />
    </div>
  );
}

function Nav() {
  const { user } = useAuth();
  return (
    <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-obsidian/70 border-b border-white/10">
      <div className="container mx-auto h-16 flex items-center justify-between px-4">
        <Logo />
        <div className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.2em] font-bold text-white/60">
          <a href="#venues" className="hover:text-volt transition-colors">Venues</a>
          <a href="#testimonials" className="hover:text-volt transition-colors">Reviews</a>
          <a href="#app" className="hover:text-volt transition-colors">Get the App</a>
          <a href="#contact" className="hover:text-volt transition-colors">Contact</a>
        </div>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to={dashboardPathFor(user)} data-testid="nav-dashboard-link">
              <Button size="sm">Go to Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link">
                <Button variant="outline" size="sm">Sign In</Button>
              </Link>
              <Link to="/register" data-testid="nav-signup-link" className="hidden sm:inline">
                <Button size="sm">Sign Up</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

// Hudle-style search-first hero: a sport dropdown + a location trigger,
// sitting right under the headline instead of scrolling to find them.
function Hero({ sports, sportId, onSportChange, address, geoStatus, onOpenLocation }) {
  const { user } = useAuth();
  return (
    <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden grain">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-obsidian via-obsidian/70 to-obsidian/40" />
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl">
          <Badge tone="volt" className="mb-8">
            <Zap size={10} className="mr-1.5" /> Badminton Courts Near You
          </Badge>
          <h1 className="font-heading font-black uppercase leading-[0.85] tracking-tighter text-balance">
            <span className="block text-6xl sm:text-7xl lg:text-8xl">Dominate</span>
            <span className="block text-6xl sm:text-7xl lg:text-8xl">the <span className="text-volt">court.</span></span>
            <span className="block text-5xl sm:text-6xl lg:text-7xl text-white/85 mt-2">Book in 60 seconds.</span>
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
            World-class courts, elite coaching, premium memberships — at venues near you. Built for the players who refuse to lose.
          </p>

          {/* Search bar */}
          <div className="mt-10 bg-surface/90 backdrop-blur border border-white/10 p-2 flex flex-col sm:flex-row gap-2 max-w-2xl" data-testid="hero-search-bar">
            {sports.length > 1 && (
              <select
                value={sportId}
                onChange={(e) => onSportChange(e.target.value)}
                className="h-12 px-4 bg-transparent text-sm text-white outline-none sm:w-48 sm:border-r sm:border-white/10"
                data-testid="hero-sport-select"
              >
                <option value="" className="bg-obsidian">All sports</option>
                {sports.map((s) => (<option key={s.id} value={s.id} className="bg-obsidian">{s.name}</option>))}
              </select>
            )}
            <button
              type="button"
              onClick={onOpenLocation}
              className="flex-1 flex items-center gap-2 px-4 h-12 text-left text-sm"
              data-testid="hero-location-button"
            >
              <MapPin size={15} className="text-volt shrink-0" />
              <span className={address ? 'text-white truncate' : 'text-white/40'}>
                {address || (geoStatus === 'locating' ? 'Detecting…' : 'Search by city or area')}
              </span>
            </button>
            <a href="#venues">
              <Button className="w-full sm:w-auto h-12" data-testid="hero-search-submit">
                Find Venues <ArrowRight size={16} />
              </Button>
            </a>
          </div>

          <div className="mt-6 flex flex-wrap gap-4 items-center">
            <Link to={user ? dashboardPathFor(user) : '/register'}>
              <Button variant="outline" size="lg" data-testid="hero-cta-signup">
                {user ? 'Go to Dashboard' : 'Sign Up Free'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// Playo-style venue discovery: a grid of nearby (or, with no location
// picked yet, all) venues — filtered by the hero's sport selection above.
// This replaces the old single-academy "our courts"/"our plans" sections —
// those don't generalize once each venue has its own courts, plans and
// pricing, so actual booking happens after sign-up + venue selection (see
// pages/customer/Venues.jsx). This section's only job is "is there a venue
// near me, worth signing up for" — a public preview, not the booking flow.
function VenueDiscovery({ venues, loading, sportId, onClearSport }) {
  const display = venues.slice(0, 6);

  return (
    <section id="venues" className="section relative">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <Badge className="mb-4">Venues</Badge>
            <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">
              Find a venue <span className="text-volt">near you.</span>
            </h2>
            <p className="mt-3 text-white/60 max-w-xl">Every venue runs its own courts, coaching and membership plans — pick one to see what's on offer.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm py-8">Loading venues…</div>
        ) : display.length === 0 ? (
          <div className="text-white/40 text-sm py-8 space-y-3">
            <p>{sportId ? 'No venues offer that sport yet.' : 'No venues published yet — check back soon.'}</p>
            {sportId && <button type="button" onClick={onClearSport} className="text-volt hover:underline">Show all sports</button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {display.map((v) => (
              <div key={v.id} className="bg-surface border border-white/10 p-6 flex flex-col gap-3" data-testid={`landing-venue-${v.id}`}>
                <div className="w-10 h-10 rounded-full bg-volt/10 flex items-center justify-center">
                  <MapPin size={18} className="text-volt" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl uppercase mb-1">{v.name}</h3>
                  {(v.address || v.city) && (
                    <p className="text-xs text-white/50 line-clamp-2">{[v.address, v.city].filter(Boolean).join(', ')}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5">
                    {v.distance_km != null && (
                      <div className="text-xs text-volt flex items-center gap-1">
                        <Navigation size={11} /> {v.distance_km} km away
                      </div>
                    )}
                    {v.review_count > 0 && (
                      <div className="text-xs text-white/50 flex items-center gap-1">
                        <Star size={11} className="text-volt fill-volt" /> {v.average_rating} ({v.review_count})
                      </div>
                    )}
                  </div>
                </div>
                <Link to="/register" className="mt-auto text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-volt transition-colors">
                  Sign up to book →
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// Real platform numbers, not fabricated ones — see GET /public-stats.
// Shows nothing (rather than a loading flicker or zeros) until the numbers
// actually arrive.
function StatsBar() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/public-stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;

  const items = [
    { icon: Calendar, label: 'Bookings Made', value: stats.total_bookings.toLocaleString('en-IN') },
    { icon: Users, label: 'Players', value: stats.total_players.toLocaleString('en-IN') },
    { icon: Building2, label: 'Venues', value: stats.total_venues.toLocaleString('en-IN') },
    { icon: Star, label: 'Avg. Rating', value: stats.average_rating != null ? `${stats.average_rating}★` : '—' },
  ];
  return (
    <section className="border-t border-white/10">
      <div className="container mx-auto px-4 py-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it) => <KPI key={it.label} label={it.label} value={it.value} icon={it.icon} />)}
      </div>
    </section>
  );
}

function FeatureHighlights() {
  const items = [
    { icon: Zap, label: 'Instant Booking', desc: 'Reserve any court slot in under 60 seconds — Razorpay-powered checkout.' },
    { icon: Trophy, label: 'Tournament Ready', desc: 'BWF-spec courts with professional lighting and wooden flooring.' },
    { icon: ShieldCheck, label: 'Email Receipts', desc: 'Get bookings, payment receipts and reminders directly on Email.' },
    { icon: Sparkles, label: 'Member Discounts & Offers', desc: 'Save on every booking with membership discounts and running venue offers.' },
  ];
  return (
    <section className="section bg-surface border-t border-b border-white/10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {items.map((it) => (
            <div key={it.label} className="bg-surface p-8 group hover:bg-surface-raised transition-colors">
              <it.icon size={28} className="text-volt mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-heading font-bold uppercase text-lg mb-2">{it.label}</h3>
              <p className="text-sm text-white/50 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function Testimonials() {
  const reviews = [
    { name: 'Karthik R.', city: 'Bengaluru', role: 'State Champion', quote: 'Best courts in the city. Booking takes seconds and the lighting is perfect for tournaments.' },
    { name: 'Priya N.', city: 'Chennai', role: 'Pro Member', quote: 'The membership discount paid for itself in 3 weeks. Coaching here transformed my game.' },
    { name: 'Arjun S.', city: 'Hyderabad', role: 'Weekend Warrior', quote: 'Email reminders are a lifesaver. Never miss a booking now.' },
    { name: 'Meera K.', city: 'Bengaluru', role: 'Club Player', quote: 'Every match here feels like coming home — the community keeps me coming back every week.' },
    { name: 'Rohit V.', city: 'Pune', role: 'Corporate League Captain', quote: 'Booked courts for our whole office league in minutes. Zero back-and-forth with the venue.' },
    { name: 'Sana T.', city: 'Chennai', role: 'Beginner', quote: 'Picked up the sport here from scratch — the coaches and the peak/off-peak pricing made it easy to start.' },
  ];
  return (
    <section id="testimonials" className="section border-t border-white/10">
      <div className="container mx-auto px-4">
        <Badge className="mb-4">Player Voices</Badge>
        <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight mb-16">
          What our members say.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r) => (
            <div key={r.name} className="p-8 bg-surface border border-white/10">
              <Quote size={28} className="text-volt mb-6" />
              <p className="text-white/80 leading-relaxed mb-6">{r.quote}</p>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-heading font-bold uppercase">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">{r.role}</div>
                </div>
                <Badge>{r.city}</Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// App-download banner — the platform has a companion mobile app; this is its
// marketing-page presence, mirroring Hudle's own app-download CTA.
function AppDownload() {
  const { app_name } = useBranding();
  return (
    <section id="app" className="section border-t border-white/10 bg-surface">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <Badge tone="volt" className="mb-4"><Smartphone size={10} className="mr-1.5" /> Mobile App</Badge>
            <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">
              Book on the go.
            </h2>
            <p className="mt-4 text-white/60 max-w-md leading-relaxed">
              Everything on the web app, in your pocket — find venues, book courts, manage memberships, and get instant Email + push notifications the moment your slot is confirmed.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" className="inline-flex items-center gap-2 border border-white/20 hover:border-volt px-5 py-3 transition-colors" data-testid="app-download-ios">
                <Smartphone size={16} className="text-volt" />
                <span className="text-sm font-bold uppercase tracking-wide">App Store</span>
              </button>
              <button type="button" className="inline-flex items-center gap-2 border border-white/20 hover:border-volt px-5 py-3 transition-colors" data-testid="app-download-android">
                <Smartphone size={16} className="text-volt" />
                <span className="text-sm font-bold uppercase tracking-wide">Google Play</span>
              </button>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="w-56 sm:w-64 aspect-[9/19] bg-obsidian border border-white/10 rounded-[2rem] p-3 shadow-2xl">
              <div className="w-full h-full rounded-[1.5rem] bg-gradient-to-br from-surface to-obsidian border border-white/10 flex flex-col items-center justify-center gap-3 p-6 text-center">
                <img src="/paceslot-mark.svg" alt="" className="h-12 w-auto" />
                <Wordmark name={app_name} className="text-lg tracking-[0.15em]" />
                <div className="text-white/40 text-[11px]">Book courts, memberships & more — right from your phone.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { user } = useAuth();
  return (
    <section className="section bg-volt text-black grain relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10 text-center">
        <h2 className="font-heading font-black uppercase text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85]">
          Dominate <br className="md:hidden" />the court.
        </h2>
        <p className="mt-6 text-lg max-w-xl mx-auto text-black/70">Sign up free. Book your first court in 60 seconds. No commitments.</p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to={user ? dashboardPathFor(user) : '/register'} data-testid="cta-register">
            <Button size="lg" className="bg-black text-volt hover:bg-black/90">
              {user ? 'Go to Dashboard' : 'Get Started Free'} <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// Fix (multi-venue): this used to hardcode one physical address/hours,
// which was accurate for a single academy but misleading now that the
// platform hosts many independently-run venues, each with its own address
// and hours (see AdminVenues.jsx). Swapped for the platform's own support
// contact — pulled from branding settings, same source Admin → Settings
// writes to — since there's no longer one venue to "visit".
function Footer() {
  const { app_name, support_email, support_phone } = useBranding();
  return (
    <footer id="contact" className="bg-obsidian border-t border-white/10">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div className="md:col-span-2">
            <Logo />
            <p className="mt-6 text-white/50 text-sm max-w-md leading-relaxed">
              {app_name} — find and book badminton venues near you. Courts, coaching and memberships, all in one place.
            </p>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Support</h4>
            <div className="space-y-2 text-sm text-white/70">
              {support_phone && (
                <div className="flex items-center gap-2"><Phone size={14} className="text-volt" /> {support_phone}</div>
              )}
              {support_email && (
                <div className="flex items-center gap-2"><Mail size={14} className="text-volt" /> {support_email}</div>
              )}
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Kalgyan Solutions. {app_name} is a product of Kalgyan Solutions.</p>
          <div className="flex gap-6 text-[10px] uppercase tracking-[0.25em] text-white/40">
            <Link to="/privacy" className="hover:text-volt transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-volt transition-colors">Terms</Link>
            <Link to="/support" className="hover:text-volt transition-colors">Support</Link>
            <Link to="/refund-policy" className="hover:text-volt transition-colors">Refund Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
